// src/App.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './utils/firebase';
import ProtectedRoute from './routes/ProtectedRoute';
import { AuthProvider } from './contexts/authContext';
import { clearUserCache } from './utils/userDataCache';
import SessionWarning from './components/SessionWarning';
import { logReadSummary } from './utils/readMeter';
import { subscribeCacheUpdates } from './utils/cacheBus';
import { initializeSecureSession } from './utils/sessionManagement';

// Pages
import LandingPage from './pages/LandingPage';
import SignUpPage from './pages/SignUpPage';
import LoginPage from './pages/LoginPage';
import HostDashboard from './pages/HostDashboard';
import HelpDesk from './pages/HelpDesk';
import AudienceDisplay from './pages/AudienceDisplay';
import LaunchGame from './pages/LaunchGame';
import HostProfile from './pages/HostProfile';
import PublicHostProfile from './pages/PublicHostProfile';
import UpcomingShows from './pages/UpcomingShows';
import Marketplace from './pages/Marketplace';
import Supplies from './pages/Supplies';
import Merchandise from './pages/Merchandise';
import AddGame from './pages/AddGame';

import MyGames from './pages/MyGames';
import FAQ from './pages/FAQ';
import TeamDashboard from './pages/TeamDashboard';
import TeamCreation from './pages/TeamCreation';
import TeamProfile from './pages/TeamProfile';
import StripeConnectOnboarding from './pages/StripeConnectOnboarding';
import TeamLeaderboards from './pages/TeamLeaderboards';
import PublicTeamProfile from './pages/PublicTeamProfile';
import TeamSwagStore from './pages/TeamSwagStore';
import TeamHelp from './pages/TeamHelp';
import TeamFeed from './pages/TeamFeed';
import VenueDiscovery from './pages/VenueDiscovery';
import GameRating from './pages/GameRating';
import AdminDashboard from './pages/AdminDashboard';
import NotAuthorized from './components/NotAuthorized';
import AdminSetup from './pages/AdminSetup';
import AdminUsers from './pages/AdminUsers';
import AdminUserDetail from './pages/AdminUserDetail';
import SupportTickets from './pages/SupportTickets';
import SupportTicket from './pages/SupportTicket';
import AdminSupportTickets from './pages/AdminSupportTickets';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminAds from './pages/AdminAds';
import AdminModeration from './pages/AdminModeration';
import AdminMarketplace from './pages/AdminMarketplace';
import AdminAIChat from './pages/AdminAIChat';
import AdminPublicGames from './pages/AdminPublicGames';
import VenueProfile from './pages/VenueProfile';
import AdminSecurity from './pages/AdminSecurity';
import EditGame from './pages/EditGame';
import CreatorDashboard from './pages/CreatorDashboard';
import AccountSettings from './pages/AccountSettings';
import PlayerHub from './pages/PlayerHub';
import EmailVerification from './pages/EmailVerification';
import InitialEmailVerification from './pages/InitialEmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import AdminBadgeManagement from './pages/AdminBadgeManagement';
import LaunchSuccess from './pages/LaunchSuccess';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import BillingSubscribe from './pages/BillingSubscribe';
import BillingLayout from './layouts/BillingLayout';
import Version from './pages/Version';
import RouteLogger from './components/RouteLogger';
import PreviewGame from './pages/PreviewGame';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

// CSP debugging completed - Firebase Realtime Database issues resolved

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    // Initialize cache bus for cross-tab synchronization
    const unsubscribeCache = subscribeCacheUpdates(({ key }) => {
      if (key === 'user:bundle') {
        console.log('📡 CACHE BUS: Received user bundle update, refreshing local cache');
        // The cache is already updated in localStorage, just need to refresh UI
        // This will trigger a re-render with fresh data
      }
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Initialize secure session for authenticated users
        try {
          const sessionData = {
            ipAddress: 'client-side', // Will be set by server
            userAgent: navigator.userAgent,
            location: 'client-side', // Will be set by server
            userRole: user.role || 'player'
          };
          
          const session = await initializeSecureSession(user.uid, sessionData);
          setSessionId(session.sessionId);
        } catch (error) {
          console.error('Error initializing session:', error);
        }
      } else {
        // Clear all caches on logout for security
        if (currentUser) {
          clearUserCache(currentUser.uid);
          // clearAuthCache(currentUser.uid); // Removed - using new auth system
          console.log('🗑️ CACHE: All caches cleared on logout');
          
          // Log read summary before clearing
          logReadSummary();
        }
        setSessionId(null);
      }
    });
    return () => {
      unsubscribe();
      unsubscribeCache();
    };
  }, [currentUser]); // Add currentUser as dependency

  return (
    <AuthProvider>
      {/* Route Logger for debugging */}
      <RouteLogger />
      
      {/* Session Warning Component */}
      {currentUser && sessionId && (
        <SessionWarning 
          userId={currentUser.uid} 
          sessionId={sessionId}
          onSessionExpired={() => {
            // Handle session expiration
            setCurrentUser(null);
            setSessionId(null);
          }}
        />
      )}
      
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/player-hub" element={<PlayerHub />} />
      <Route path="/profile/:hostId" element={<PublicHostProfile />} />
      <Route path="/not-authorized" element={<NotAuthorized />} />

      {/* Host Routes */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HostDashboard />
        </ProtectedRoute>
      } />
      <Route path="/helpdesk" element={
        <ProtectedRoute>
          <HelpDesk />
        </ProtectedRoute>
      } />
      <Route path="/audience/:id" element={<AudienceDisplay />} />
      <Route path="/launch/:id" element={
        <ProtectedRoute>
          <LaunchGame />
        </ProtectedRoute>
      } />
      <Route path="/launch-game/:id" element={
        <ProtectedRoute>
          <LaunchGame />
        </ProtectedRoute>
      } />
      <Route path="/launch-success" element={
        <ProtectedRoute>
          <LaunchSuccess />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <HostProfile />
        </ProtectedRoute>
      } />
      <Route path="/host-profile/:hostId" element={<HostProfile />} />
      <Route path="/upcoming-shows" element={
        <ProtectedRoute>
          <UpcomingShows />
        </ProtectedRoute>
      } />
      <Route path="/marketplace" element={
        <ProtectedRoute>
          <Marketplace />
        </ProtectedRoute>
      } />
      <Route path="/supplies" element={
        <ProtectedRoute>
          <Supplies />
        </ProtectedRoute>
      } />
      <Route path="/merch" element={
        <ProtectedRoute>
          <Merchandise />
        </ProtectedRoute>
      } />
      <Route path="/add-game" element={
        <ProtectedRoute>
          <AddGame />
        </ProtectedRoute>
      } />
      <Route path="/admin/add-game" element={
        <ProtectedRoute requireAdmin>
          <AddGame />
        </ProtectedRoute>
      } />

      <Route path="/my-games" element={
        <ProtectedRoute>
          <MyGames />
        </ProtectedRoute>
      } />
      <Route path="/stripe-connect-onboarding" element={
        <ProtectedRoute>
          <StripeConnectOnboarding />
        </ProtectedRoute>
      } />
        <Route path="/faq" element={<FAQ />} />

      {/* Team Platform Routes (Simplified League System) */}
      <Route path="/team" element={<TeamDashboard />} />
      <Route path="/team/dashboard" element={<TeamDashboard />} />
      <Route path="/team/create" element={<ProtectedRoute element={<TeamCreation />} />} />
      <Route path="/team/profile" element={<ProtectedRoute element={<TeamProfile />} />} />
      <Route path="/team/leaderboards" element={<ProtectedRoute element={<TeamLeaderboards />} />} />
      <Route path="/team/join" element={<ProtectedRoute element={<TeamProfile />} />} />
      <Route path="/team/public/:leagueId" element={<PublicTeamProfile />} />
      <Route path="/team/swag" element={<ProtectedRoute element={<TeamSwagStore />} />} />
      <Route path="/team/help" element={<ProtectedRoute element={<TeamHelp />} />} />
      <Route path="/team/feed" element={<ProtectedRoute element={<TeamFeed />} />} />
      <Route path="/venue-discovery" element={
        <ProtectedRoute>
          <VenueDiscovery />
        </ProtectedRoute>
      } />

      {/* Rating Route (Public - accessed via QR code) */}
      <Route path="/rate-game/:gameId/:hostId" element={<GameRating />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/setup" element={
        <ProtectedRoute requireAdmin>
          <AdminSetup />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/users/:userId" element={
        <ProtectedRoute requireAdmin>
          <AdminUserDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/support" element={
        <ProtectedRoute requireAdmin>
          <SupportTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/support/:id" element={
        <ProtectedRoute requireAdmin>
          <SupportTicket />
        </ProtectedRoute>
      } />
      <Route path="/admin/support-tickets" element={
        <ProtectedRoute requireAdmin>
          <AdminSupportTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requireAdmin>
          <AdminAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/admin/ads" element={
        <ProtectedRoute requireAdmin>
          <AdminAds />
        </ProtectedRoute>
      } />
      <Route path="/admin/moderation" element={
        <ProtectedRoute requireAdmin>
          <AdminModeration />
        </ProtectedRoute>
      } />
      <Route path="/admin/marketplace" element={
        <ProtectedRoute requireAdmin>
          <AdminMarketplace />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-chat" element={
        <ProtectedRoute requireAdmin>
          <AdminAIChat />
        </ProtectedRoute>
      } />
      <Route path="/admin/public-games" element={
        <ProtectedRoute requireAdmin>
          <AdminPublicGames />
        </ProtectedRoute>
      } />
      <Route path="/admin/venue/:id" element={
        <ProtectedRoute requireAdmin>
          <VenueProfile />
        </ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute requireAdmin>
          <AdminSecurity />
        </ProtectedRoute>
      } />
      <Route path="/admin/badges" element={
        <ProtectedRoute requireAdmin>
          <AdminBadgeManagement />
        </ProtectedRoute>
      } />
      <Route path="/edit-game/:id" element={
        <ProtectedRoute>
          <EditGame />
        </ProtectedRoute>
      } />

      {/* Support Routes */}
      <Route path="/support-ticket" element={<SupportTicket />} />

      {/* Creator Dashboard Routes - Redirect to main dashboard */}
      <Route path="/creator" element={<Navigate to="/home" replace />} />
      <Route path="/creator-dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      
      {/* Billing Routes - Public (no auth required) */}
      <Route path="/billing" element={<BillingLayout />}>
        <Route path="subscribe" element={<BillingSubscribe />} />
        <Route path="subscribe/:plan" element={<BillingSubscribe />} />
        <Route path="success" element={<BillingSuccess />} />
        <Route path="cancel" element={<BillingCancel />} />
      </Route>
      
      {/* Debug routes to test routing */}
      <Route path="/test-billing" element={<div>Billing route test works!</div>} />
      <Route path="/__version" element={<Version />} />
      
      {/* Account Settings Route */}
      <Route path="/account-settings" element={
        <ProtectedRoute>
          <AccountSettings />
        </ProtectedRoute>
      } />
      
      {/* Email Verification Route */}
      <Route path="/verify-email/:requestId" element={<EmailVerification />} />
      
      {/* Initial Email Verification Route */}
      <Route path="/verify-initial" element={<InitialEmailVerification />} />
      
      {/* Preview Mode Route */}
      <Route path="/preview/:gameId" element={
        <ProtectedRoute>
          <PreviewGame />
        </ProtectedRoute>
      } />
      
      {/* Not Authorized Route */}
      <Route path="/not-authorized" element={<NotAuthorized />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
    </Routes>
    </AuthProvider>
  );
};

export default App;
